// Words and numbers for the floating-price formula screens (ledger row 132).
//
// Display only. Every price here is a string the server calculated
// (``market_pricing/services/floating_price.py`` is the one place the
// arithmetic lives); this file groups thousands and picks the sentence, and
// never does sums on money.

import { canonicalizeAmount, stripTrailingZeros } from '../../../utils/amountFormat';
import { extractApiError } from '../../../utils/errorMessages';

// The server's own sentence, word for word (``extractApiError`` reads every
// shape the API refuses in, and never prints "[object Object]"). A body that
// is not the API's -- a proxy's HTML error page during a deploy -- is never
// shown as text; the trader gets ``fallback`` instead.
export const serverSentence = (err, fallback) => (
  typeof err?.response?.data === 'string' ? fallback : extractApiError(err, fallback)
);

export const SIDE_LABEL = { sell: 'Sales side', buy: 'Purchase side' };

export const PRICING_OPTIONS = [
  { value: 'whole_month', label: 'Whole-month average (WMA)' },
  { value: 'single_day', label: 'Day of delivery (one date)' },
  { value: 'date_range', label: 'Date range average' },
];

const PRICE_POINTS = [
  { value: 'c', label: 'Close' },
  { value: 'h', label: 'High' },
  { value: 'l', label: 'Low' },
];

// Index ('u') exists in the price list but no desk formula uses it today. It
// is offered only when the benchmark or the saved formula already speaks it,
// so the picker can always show the value it holds.
export const pricePointsFor = (...inUse) => (
  inUse.includes('u') ? [...PRICE_POINTS, { value: 'u', label: 'Index' }] : PRICE_POINTS
);

// Results that carry no price. They are shown as a sentence and never as a
// number: a 0.00 here would read as a real price.
const NO_PRICE_STATUSES = new Set(['refused', 'no_prices', 'waiting_for_date']);

export const resultHasPrice = (result) => Boolean(
  result
  && !NO_PRICE_STATUSES.has(result.status)
  && canonicalizeAmount(result.final_price) !== '',
);

const groupThousands = (digits) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

// "1173.68" -> "1,173.68". Keeps every decimal the server sent: a final price
// of "744.00" stays "744.00".
export const formatPrice = (value) => {
  const canon = canonicalizeAmount(value);
  if (canon === '') return '';
  const negative = canon.startsWith('-');
  const [whole, fraction] = (negative ? canon.slice(1) : canon).split('.');
  return `${negative ? '-' : ''}${groupThousands(whole)}${fraction !== undefined ? `.${fraction}` : ''}`;
};

// A typed price shown next to a calculated one: always two decimals, more
// only when the trader typed more.
export const formatTypedPrice = (value) => {
  const canon = canonicalizeAmount(value);
  if (canon === '') return '';
  const [whole, fraction = ''] = canon.split('.');
  return formatPrice(`${whole}.${fraction.padEnd(2, '0')}`);
};

export const priceText = (result) => (
  `${result.currency} ${formatPrice(result.final_price)}/${result.deal_unit}`
);

// The one-line status under a saved formula on the enquiry.
export const lineStatusText = (result) => {
  if (!result) return '';
  if (!resultHasPrice(result)) return result.message;
  const price = priceText(result);
  if (result.status === 'final') return `Final price ${price}`;
  if (result.status === 'provisional') return `${result.message} Currently ${price}.`;
  return `${result.message} On the prices uploaded so far: ${price}.`;
};

// The heading over the big number in the formula window.
export const finalPriceLabel = (result) => {
  if (result.status === 'final') return 'Final floating price';
  if (result.status === 'provisional') return 'Provisional floating price';
  return 'Price on the prices uploaded so far (not final)';
};

// Same number, same decimals? Compared as text so no float ever decides it.
export const samePrice = (a, b) => stripTrailingZeros(a) === stripTrailingZeros(b);

export const normaliseUnit = (unit) => (unit || '').trim().toUpperCase();

// Tonnage on the enquiry is always metric tonnes (the MIN / MAX fields say
// so), whatever the line's packaging unit.
const TONNAGE_UNIT = 'MT';

const toTonnes = (value) => {
  const canon = canonicalizeAmount(value);
  return canon === '' ? null : Number(canon);
};

const formatTonnes = (value) => value.toLocaleString('en-US', { maximumFractionDigits: 3 });

// "500 MT", "480–500 MT", or null when neither end is entered.
export const quantityText = (minTonnage, maxTonnage) => {
  const low = toTonnes(minTonnage);
  const high = toTonnes(maxTonnage);
  if (low === null && high === null) return null;
  if (low === null || high === null || low === high) {
    return `${formatTonnes(low === null ? high : low)} ${TONNAGE_UNIT}`;
  }
  return `${formatTonnes(low)}–${formatTonnes(high)} ${TONNAGE_UNIT}`;
};

// Several lines' quantities as one total, or null when none is entered.
export const totalQuantityText = (rows) => {
  let low = 0;
  let high = 0;
  let any = false;
  rows.forEach((row) => {
    const min = toTonnes(row.min_tonnage);
    const max = toTonnes(row.max_tonnage);
    if (min === null && max === null) return;
    any = true;
    low += min === null ? max : min;
    high += max === null ? min : max;
  });
  if (!any) return null;
  return low === high
    ? `${formatTonnes(low)} ${TONNAGE_UNIT}`
    : `${formatTonnes(low)}–${formatTonnes(high)} ${TONNAGE_UNIT}`;
};

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// "2026-09-01" -> "September 2026".
export const monthLabel = (value) => {
  const [year, month] = String(value).split('-');
  return `${MONTH_NAMES[Number(month) - 1]} ${year}`;
};

// The pricing months offered: two years back (Phil's price file starts in
// April 2024) to a year ahead, grouped by year. A saved month outside that
// window is kept, so an old formula still shows what it says.
export const monthOptions = (today = new Date(), keep = []) => {
  const values = [];
  for (let year = today.getFullYear() - 2; year <= today.getFullYear() + 1; year += 1) {
    for (let month = 1; month <= 12; month += 1) {
      values.push(`${year}-${String(month).padStart(2, '0')}-01`);
    }
  }
  keep.filter(Boolean).forEach((value) => {
    if (!values.includes(value)) values.push(value);
  });
  values.sort();
  const byYear = [];
  values.forEach((value) => {
    const year = value.slice(0, 4);
    let group = byYear.find((g) => g.year === year);
    if (!group) {
      group = { year, months: [] };
      byYear.push(group);
    }
    group.months.push({ value, label: monthLabel(value) });
  });
  return byYear;
};

// The contract stores the month as its first day; a saved "2026-09-15" or
// "2026-09" still means September.
export const firstOfMonth = (value) => (value ? `${String(value).slice(0, 7)}-01` : '');
